import { useState, useEffect } from 'react';
import API, { SERVER_URL } from '../../api/axios';
import AppLayout from '../../components/layout/AppLayout';
import { S } from '../../components/pageStyles';
import { Loader, StatCard } from '../../components/ui';
import { LuBriefcase, LuConciergeBell, LuEye, LuEyeOff, LuIdCard, LuKey, LuLifeBuoy, LuLock, LuLockOpen, LuPencil, LuSmartphone, LuStore, LuUser, LuUsers, LuWallet } from 'react-icons/lu';

const ROLES = {
  gerant: { label: 'Gérant', icon: <LuBriefcase aria-hidden />, couleur: '#1D4ED8', bg: '#EFF6FF' },
  serveur: { label: 'Serveur', icon: <LuConciergeBell aria-hidden />, couleur: '#C2410C', bg: '#FFF7ED' },
  caissier: { label: 'Caissier', icon: <LuWallet aria-hidden />, couleur: '#0E7490', bg: '#ECFEFF' },
};

export default function EmployesProprietaire() {
  const [employes, setEmployes] = useState([]);
  const [etablissements, setEtablissements] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [vue, setVue] = useState('liste');
  const [employeSelectionne, setEmployeSelectionne] = useState(null);
  const [message, setMessage] = useState('');
  const [recherche, setRecherche] = useState('');
  const [filtreEtab, setFiltreEtab] = useState('tous');
  const [filtreRole, setFiltreRole] = useState('tous');
  const [afficherMotDePasse, setAfficherMotDePasse] = useState(false);
  const [form, setForm] = useState({
    nom: '', prenom: '', telephone: '',
    mot_de_passe: '', role: 'serveur',
    etablissement_id: '',
    numero_piece_identite: '',
    type_piece_identite: 'CNI',
    contact_urgence_nom: '',
    contact_urgence_tel: ''
  });
  const [photoFichier, setPhotoFichier] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const chargerDonnees = async () => {
    try {
      const [usersRes, etabRes] = await Promise.all([
        API.get('/utilisateurs'),
        API.get('/etablissements')
      ]);
      const tousUsers = usersRes.data.utilisateurs || [];
      console.log('Tous users:', tousUsers);
      const employes = tousUsers.filter(u =>
        ['gerant', 'serveur', 'caissier'].includes(u.role)
      );
      console.log('Employés filtrés:', employes);
      setEmployes(employes);
      setEtablissements(etabRes.data.etablissements || []);
    } catch (err) {
      console.error('Erreur chargement:', err);
    } finally {
      setChargement(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { chargerDonnees(); }, []);

  const handlePhoto = (e) => {
    const fichier = e.target.files[0];
    if (fichier) {
      setPhotoFichier(fichier);
      setPhotoPreview(URL.createObjectURL(fichier));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(form).forEach(key => {
        if (form[key]) formData.append(key, form[key]);
      });
      if (photoFichier) formData.append('photo_profil', photoFichier);

      if (employeSelectionne) {
        const data = Object.fromEntries(formData);
        if (!data.mot_de_passe) delete data.mot_de_passe;
        await API.put('/utilisateurs/' + employeSelectionne.id, data);
        setMessage('✅ Employé modifié avec succès !');
      } else {
        await API.post('/utilisateurs', formData);
        setMessage('✅ Employé créé avec succès !');
      }
      chargerDonnees();
      setVue('liste');
      setEmployeSelectionne(null);
      resetForm();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const handleModifier = (employe) => {
    setEmployeSelectionne(employe);
    setForm({
      nom: employe.nom || '',
      prenom: employe.prenom || '',
      telephone: employe.telephone || '',
      mot_de_passe: '',
      role: employe.role || 'serveur',
      etablissement_id: employe.etablissement_id || '',
      numero_piece_identite: employe.numero_piece_identite || '',
      type_piece_identite: employe.type_piece_identite || 'CNI',
      contact_urgence_nom: employe.contact_urgence_nom || '',
      contact_urgence_tel: employe.contact_urgence_tel || ''
    });
    if (employe.photo_profil) {
      setPhotoPreview(SERVER_URL + '/uploads/profils/' +
        employe.photo_profil.replace(/\\/g, '/').split('/').pop());
    }
    setVue('formulaire');
  };

  const handleToggle = async (id, actif) => {
    if (!window.confirm(actif ? 'Désactiver cet employé ?' : 'Réactiver cet employé ?')) return;
    try {
      await API.patch('/utilisateurs/' + id + '/toggle');
      setMessage(actif ? '✅ Employé désactivé !' : '✅ Employé réactivé !');
      chargerDonnees();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const resetForm = () => {
    setForm({
      nom: '', prenom: '', telephone: '',
      mot_de_passe: '', role: 'serveur',
      etablissement_id: '',
      numero_piece_identite: '',
      type_piece_identite: 'CNI',
      contact_urgence_nom: '',
      contact_urgence_tel: ''
    });
    setPhotoFichier(null);
    setPhotoPreview(null);
    setAfficherMotDePasse(false);
  };

  const getEtabNom = (id) => {
    const etab = etablissements.find(e => e.id === id);
    return etab ? etab.nom : '—';
  };

  const employesFiltres = employes.filter(e => {
    const matchRecherche = (e.nom + ' ' + e.prenom).toLowerCase()
      .includes(recherche.toLowerCase()) ||
      (e.telephone || '').includes(recherche);
    const matchEtab = filtreEtab === 'tous' ||
      e.etablissement_id === parseInt(filtreEtab);
    const matchRole = filtreRole === 'tous' || e.role === filtreRole;
    return matchRecherche && matchEtab && matchRole;
  });

  if (chargement) return <Loader />;

  return (
    <AppLayout role="proprietaire">

      {/* CONTENU */}
      <div style={styles.contenu}>

        {message && (
          <div role="status" style={{
            ...styles.messageBox,
            background: message.startsWith('✅') ? '#ECFDF5' : '#FEF2F2',
            color: message.startsWith('✅') ? '#047857' : '#B91C1C'
          }}>{message.replace(/^(✅|❌|⚠️)\s*/, '')}</div>
        )}

        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitre}>Mes Employés</h1>
            <p style={styles.headerSousTitre}>
              {employes.length + ' employé(s) • ' +
               employes.filter(e => e.actif).length + ' actifs'}
            </p>
          </div>
          {vue === 'liste' ? (
            <button
              onClick={() => { resetForm(); setEmployeSelectionne(null); setVue('formulaire'); }}
              style={styles.boutonAjouter}>
              + Nouvel Employé
            </button>
          ) : (
            <button onClick={() => setVue('liste')} style={styles.boutonRetour}>
              ← Retour
            </button>
          )}
        </div>

        {/* STATS PAR RÔLE */}
        {vue === 'liste' && (
          <div style={styles.statsGrid}>
            {Object.entries(ROLES).map(([role, config]) => {
              const count = employes.filter(e => e.role === role).length;
              return (
                <StatCard key={role} icon={config.icon} label={config.label + 's'} value={count} color={config.couleur} />
              );
            })}
          </div>
        )}

        {/* ── LISTE ── */}
        {vue === 'liste' && (
          <>
            <div style={styles.filtres}>
              <input
                type="text"
                placeholder="Rechercher par nom ou téléphone..."
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                style={styles.rechercheInput}
              />
              <select
                value={filtreRole}
                onChange={(e) => setFiltreRole(e.target.value)}
                style={styles.filtreSelect}
              >
                <option value="tous">Tous les rôles</option>
                {Object.entries(ROLES).map(([role, config]) => (
                  <option key={role} value={role}>{config.label}</option>
                ))}
              </select>
              <select
                value={filtreEtab}
                onChange={(e) => setFiltreEtab(e.target.value)}
                style={styles.filtreSelect}
              >
                <option value="tous">Tous les établissements</option>
                {etablissements.map(e => (
                  <option key={e.id} value={e.id}>{e.nom}</option>
                ))}
              </select>
            </div>

            <div style={styles.section}>
              {employes.length === 0 ? (
                <div style={styles.vide}>
                  <div style={{ fontSize: 48 }}><LuUsers aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                  <p style={{ fontWeight: 700, fontSize: 16 }}>Aucun employé trouvé</p>
                  <p style={{ color: '#94A3B8', fontSize: 13 }}>
                    Créez votre premier employé en cliquant sur "+ Nouvel Employé"
                  </p>
                </div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      {['Photo', 'Employé', 'Rôle', 'Établissement',
                        'Téléphone', 'Statut', 'Actions'].map((h, i) => (
                        <th key={i} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {employesFiltres.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#64748B' }}>
                          Aucun résultat pour cette recherche
                        </td>
                      </tr>
                    ) : (
                      employesFiltres.map((emp, i) => {
                        const role = ROLES[emp.role] || ROLES.serveur;
                        return (
                          <tr key={emp.id} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                            <td style={styles.td}>
                              {emp.photo_profil ? (
                                <img
                                  src={SERVER_URL + '/uploads/profils/' +
                                    emp.photo_profil.replace(/\\/g, '/').split('/').pop()}
                                  alt="profil"
                                  style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                                />
                              ) : (
                                <div style={styles.avatarPlaceholder}>
                                  {(emp.prenom || '?')[0]}{(emp.nom || '?')[0]}
                                </div>
                              )}
                            </td>
                            <td style={styles.td}>
                              <div style={{ fontWeight: 700 }}>
                                {emp.prenom + ' ' + emp.nom}
                              </div>
                              {emp.contact_urgence_nom && (
                                <div style={{ color: '#64748B', fontSize: 11 }}>
                                  {' ' + emp.contact_urgence_nom}
                                </div>
                              )}
                            </td>
                            <td style={styles.td}>
                              <span style={{
                                padding: '3px 10px', borderRadius: 12,
                                fontSize: 12, fontWeight: 700,
                                background: role.bg, color: role.couleur
                              }}>
                                {role.icon} {role.label}
                              </span>
                            </td>
                            <td style={styles.td}>{getEtabNom(emp.etablissement_id)}</td>
                            <td style={styles.td}>
                              <span style={{ fontFamily: 'monospace' }}>{emp.telephone}</span>
                            </td>
                            <td style={styles.td}>
                              <span style={{
                                padding: '3px 10px', borderRadius: 12,
                                fontSize: 12, fontWeight: 700,
                                background: emp.actif ? '#ECFDF5' : '#FEF2F2',
                                color: emp.actif ? '#059669' : '#DC2626'
                              }}>
                                {emp.actif ? 'Actif' : 'Inactif'}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={() => handleModifier(emp)} style={styles.btnModifier} aria-label="Modifier" title="Modifier">
                                  <LuPencil aria-hidden style={{ verticalAlign: '-0.125em' }} />
                                </button>
                                <button
                                  onClick={() => handleToggle(emp.id, emp.actif)}
                                  style={emp.actif ? styles.btnDesactiver : styles.btnActiver}
                                 aria-label={emp.actif ? 'Désactiver' : 'Activer'} title={emp.actif ? 'Désactiver' : 'Activer'}>
                                  {emp.actif ? <LuLock aria-hidden /> : <LuLockOpen aria-hidden />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* ── FORMULAIRE ── */}
        {vue === 'formulaire' && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitre}>
              {employeSelectionne ? "Modifier l'employé" : 'Nouvel Employé'}
            </h2>
            <form onSubmit={handleSubmit}>

              {/* IDENTITÉ */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuUser aria-hidden style={{ verticalAlign: '-0.125em' }} /> Identité</div>
                <div style={styles.photoSection}>
                  <div style={styles.photoBox}>
                    {photoPreview ? (
                      <img src={photoPreview} alt="profil"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    ) : (
                      <div style={styles.photoPlaceholderRond}>
                        <span style={{ fontSize: 36 }}><LuUser aria-hidden style={{ verticalAlign: '-0.125em' }} /></span>
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={handlePhoto} style={styles.photoInput} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={styles.formGrid}>
                      <div style={styles.champGroupe}>
                        <label style={styles.label}>Prénom *</label>
                        <input type="text" value={form.prenom}
                          onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                          style={styles.input} placeholder="Ex: Moussa" required />
                      </div>
                      <div style={styles.champGroupe}>
                        <label style={styles.label}>Nom *</label>
                        <input type="text" value={form.nom}
                          onChange={(e) => setForm({ ...form, nom: e.target.value })}
                          style={styles.input} placeholder="Ex: Koné" required />
                      </div>
                      <div style={styles.champGroupe}>
                        <label style={styles.label}><LuSmartphone aria-hidden style={{ verticalAlign: '-0.125em' }} /> Téléphone *</label>
                        <input type="text" value={form.telephone}
                          onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                          style={styles.input} placeholder="+225 07 00 00 00" required />
                      </div>
                      <div style={styles.champGroupe}>
                        <label style={styles.label}>
                          <LuKey aria-hidden style={{ verticalAlign: '-0.125em' }} /> {employeSelectionne ? 'Nouveau mot de passe' : 'Mot de passe *'}
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type={afficherMotDePasse ? 'text' : 'password'}
                            value={form.mot_de_passe}
                            onChange={(e) => setForm({ ...form, mot_de_passe: e.target.value })}
                            style={{ ...styles.input, paddingRight: 44 }}
                            placeholder={employeSelectionne ? 'Laisser vide = inchangé' : 'Mot de passe...'}
                            required={!employeSelectionne}
                          />
                          <button type="button"
                            onClick={() => setAfficherMotDePasse(!afficherMotDePasse)}
                            aria-label={afficherMotDePasse ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                            style={styles.btnOeil}>
                            {afficherMotDePasse ? <LuEyeOff aria-hidden /> : <LuEye aria-hidden />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RÔLE & ÉTABLISSEMENT */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /> Rôle & Établissement</div>
                <div style={styles.formGrid}>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Rôle *</label>
                    <select value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      style={styles.select} required>
                      {Object.entries(ROLES).map(([role, config]) => (
                        <option key={role} value={role}>
                          {config.label}
                        </option>
                      ))}
                    </select>
                    <div style={{
                      marginTop: 6, padding: '6px 12px',
                      background: ROLES[form.role]?.bg,
                      color: ROLES[form.role]?.couleur,
                      borderRadius: 8, fontSize: 12
                    }}>
                      {form.role === 'gerant' && 'Gère produits + stocks + ventes'}
                      {form.role === 'serveur' && 'Effectue des ventes uniquement'}
                      {form.role === 'caissier' && 'Effectue des ventes uniquement'}
                    </div>
                  </div>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /> Établissement *</label>
                    <select value={form.etablissement_id}
                      onChange={(e) => setForm({ ...form, etablissement_id: e.target.value })}
                      style={styles.select} required>
                      <option value="">Sélectionnez un établissement</option>
                      {etablissements.map(e => (
                        <option key={e.id} value={e.id}>{e.nom}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* PIÈCE D'IDENTITÉ */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuIdCard aria-hidden style={{ verticalAlign: '-0.125em' }} /> Pièce d'identité</div>
                <div style={styles.formGrid}>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Type de pièce</label>
                    <select value={form.type_piece_identite}
                      onChange={(e) => setForm({ ...form, type_piece_identite: e.target.value })}
                      style={styles.select}>
                      <option value="CNI">CNI</option>
                      <option value="Passeport">Passeport</option>
                      <option value="Permis">Permis de conduire</option>
                      <option value="Carte_sejour">Carte de séjour</option>
                    </select>
                  </div>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Numéro</label>
                    <input type="text" value={form.numero_piece_identite}
                      onChange={(e) => setForm({ ...form, numero_piece_identite: e.target.value })}
                      style={styles.input} placeholder="Ex: CI0123456789" />
                  </div>
                </div>
              </div>

              {/* CONTACT D'URGENCE */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuLifeBuoy aria-hidden style={{ verticalAlign: '-0.125em' }} /> Contact d'urgence</div>
                <div style={styles.formGrid}>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Nom du contact</label>
                    <input type="text" value={form.contact_urgence_nom}
                      onChange={(e) => setForm({ ...form, contact_urgence_nom: e.target.value })}
                      style={styles.input} placeholder="Ex: Marie Koné" />
                  </div>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Téléphone</label>
                    <input type="text" value={form.contact_urgence_tel}
                      onChange={(e) => setForm({ ...form, contact_urgence_tel: e.target.value })}
                      style={styles.input} placeholder="+225 07 00 00 00" />
                  </div>
                </div>
              </div>

              <div style={styles.formActions}>
                <button type="button" onClick={() => setVue('liste')} style={styles.btnAnnuler}>
                  Annuler
                </button>
                <button type="submit" style={styles.btnSauvegarder}>
                  {employeSelectionne ? 'Sauvegarder' : "Créer l'employé"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

const styles = {
  chargement: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 24, color: '#475569' },
  contenu: { ...S.contenu, flex: 1 },
  messageBox: { ...S.messageBox, marginBottom: 16 },
  header: S.header,
  headerTitre: S.headerTitre,
  headerSousTitre: S.headerSousTitre,
  boutonAjouter: S.boutonAjouter,
  boutonRetour: S.boutonRetour,
  statsGrid: { ...S.statsGrid, marginBottom: 20 },
  statCard: S.statCard,
  filtres: { ...S.filtres, marginBottom: 16 },
  rechercheInput: { ...S.rechercheInput },
  filtreSelect: { ...S.filtreSelect, minWidth: 160 },
  section: S.section,
  table: { ...S.table, width: '100%' },
  tableHeader: S.tableHeader,
  th: S.th,
  td: S.td,
  avatarPlaceholder: {
    width: 40, height: 40, borderRadius: '50%',
    background: 'var(--accent)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontWeight: 700, fontSize: 14
  },
  btnModifier: S.btnModifier,
  btnDesactiver: S.btnDesactiver,
  btnActiver: S.btnActiver,
  vide: S.vide,
  formCard: S.formCard,
  formTitre: { ...S.formTitre, marginBottom: 24 },
  sectionForm: { ...S.sectionForm, marginBottom: 24 },
  sectionFormTitre: { ...S.sectionFormTitre, marginBottom: 16 },
  photoSection: { display: 'flex', gap: 20, alignItems: 'flex-start' },
  photoBox: {
    width: 120, height: 120, borderRadius: '50%',
    border: '2px dashed #E2E8F0', overflow: 'hidden',
    cursor: 'pointer', position: 'relative', flexShrink: 0, background: '#F8FAFC'
  },
  photoPlaceholderRond: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  photoInput: { position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' },
  formGrid: S.formGrid,
  champGroupe: S.champGroupe,
  label: S.label,
  input: { ...S.input, width: '100%' },
  select: { ...S.select, width: '100%' },
  btnOeil: { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 },
  formActions: { ...S.formActions, marginTop: 16 },
  btnAnnuler: S.btnAnnuler,
  btnSauvegarder: S.btnSauvegarder
};